import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import './RestaurantOrders.css';

const RestaurantOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();

  // تنسيق التاريخ مترجم
  const formatTimestamp = useCallback((ts) => {
    if (!ts?.toDate) return t('not_available');
    const d = ts.toDate();
    return d.toLocaleDateString(t('locale'), {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }, [t]);

  // مراقبة الطلبات
  const watchOrders = useCallback((restaurantId) => {
    setLoading(true);
    const q = query(
      collection(db, 'orders'),
      where('restaurantId', '==', restaurantId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, snap => {
      setOrders(snap.docs.map(doc => {
        const d = doc.data();
        return {
          id: doc.id,
          orderCode: d.orderCode || doc.id,
          customerName: d.customerName,
          phone: d.phone,
          address: d.address,
          driverName: d.driverName,
          amount: d.amount,
          status: d.status,
          formattedDate: formatTimestamp(d.createdAt)
        };
      }));
      setLoading(false);
    }, err => {
      console.error(err);
      setError(t('error_loading_orders'));
      setLoading(false);
    });
  }, [formatTimestamp, t]);

  // Auth + start watchOrders
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, user => {
      if (!user) return navigate('/restaurant/login');
      const rid = localStorage.getItem('restaurantId');
      if (!rid) return navigate('/restaurant/login');
      const unsubOrders = watchOrders(rid);
      return unsubOrders;
    });
    return () => unsubAuth();
  }, [navigate, watchOrders]);

  const handleView = id => navigate(`/restaurant/orders/${id}`);
  const handleNew  = () => navigate('/restaurant/orders/new');

  const filtered = orders.filter(o => {
    if (filter==='pending'   && o.status!=='قيد التنفيذ 🚀') return false;
    if (filter==='active'    && o.status!=='تم الاستلام 🚗') return false;
    if (filter==='completed' && o.status!=='تم التسليم ✅') return false;
    if (filter==='cancelled' && o.status!=='ملغي ❌')      return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return [o.customerName, o.phone, o.address, o.orderCode]
      .some(field => field?.toLowerCase().includes(s));
  });

  const statusClass = status => {
    if (status==='تم التسليم ✅') return 'status-completed';
    if (status==='ملغي ❌')      return 'status-cancelled';
    if (status==='تم الاستلام 🚗') return 'status-picked';
    return 'status-pending';
  };

  if (loading) return <LoadingSpinner text={t('loading')} />;

  return (
    <div className="restaurant-orders-page">
      <RestaurantSidebar/>
      <div className="restaurant-orders-content">
        <RestaurantHeader restaurantName={localStorage.getItem('restaurantName')||t('restaurant')}/>
        <div className="orders-container">
          <div className="orders-header">
            <h1>{t('orders_management')}</h1>
            <button className="create-order-btn" onClick={handleNew}>
              + {t('new_order')}
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
          {/* فلاتر */}
          <div className="orders-filters">
            <div className="filter-tabs">
              {['all','pending','active','completed','cancelled'].map(key=>(
                <button
                  key={key}
                  className={`filter-tab ${filter===key?'active':''}`}
                  onClick={()=>setFilter(key)}
                >
                  {t(key==='all'? 'all' : `${key}_orders`)}
                  <span className="count">
                    { orders.filter(o=>{
                        if(key==='all') return true;
                        const map = {
                          pending:   'قيد التنفيذ 🚀',
                          active:    'تم الاستلام 🚗',
                          completed: 'تم التسليم ✅',
                          cancelled: 'ملغي ❌'
                        };
                        return o.status===map[key];
                      }).length
                    }
                  </span>
                </button>
              ))}
            </div>
            <div className="search-box">
              <input
                type="text"
                placeholder={t('search_orders')}
                value={searchTerm}
                onChange={e=>setSearchTerm(e.target.value)}
                aria-label={t('search_orders')}
              />
              <span className="search-icon">🔍</span>
            </div>
          </div>
          {/* جدول الطلبات */}
          {filtered.length===0 ? (
            <div className="no-orders">
              <p>{t('no_orders_available')} {filter!=='all'&&t('in_this_category')}</p>
              {filter!=='all'&&(
                <button className="view-all-btn" onClick={()=>setFilter('all')}>
                  {t('view_all_orders')}
                </button>
              )}
            </div>
          ):(
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>{t('order_number')}</th>
                    <th>{t('customer')}</th>
                    <th>{t('address')}</th>
                    <th>{t('driver')}</th>
                    <th>{t('amount')}</th>
                    <th>{t('date')}</th>
                    <th>{t('status')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(o=>(
                    <tr key={o.id} onClick={()=>handleView(o.id)}>
                      <td className="order-id">{o.orderCode}</td>
                      <td>
                        <div className="customer-info">
                          <span className="customer-name">{o.customerName}</span>
                          <span className="customer-phone">{o.phone}</span>
                        </div>
                      </td>
                      <td className="order-address" title={o.address}>
                        {o.address.length>25?`${o.address.slice(0,25)}...`:o.address||t('no_address')}
                      </td>
                      <td>{o.driverName||t('not_assigned')}</td>
                      <td className="order-amount">{o.amount?.toFixed(3)} {t('currency')}</td>
                      <td>{o.formattedDate}</td>
                      <td>
                        <span className={`status-badge ${statusClass(o.status)}`}>
                          {t(o.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-order-btn"
                          onClick={e => { e.stopPropagation(); handleView(o.id); }}
                        >
                          {t('view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* إحصائيات سريعة */}
          <div className="orders-stats-section">
            {[ 
              {label:'total_orders', value: orders.length},
              {label:'completed_orders', value: orders.filter(o=>o.status==='تم التسليم ✅').length},
              {label:'active_orders',    value: orders.filter(o=>o.status==='تم الاستلام 🚗').length},
              {label:'cancelled_orders', value: orders.filter(o=>o.status==='ملغي ❌').length},
            ].map(s=>(
              <div key={s.label} className="stats-card">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{t(s.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantOrders;
